import { useState } from "react";
import { BuyerSearchResult } from "@/types/buyerSearch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, Building2, TrendingUp, Clock, Phone, Mail, UserPlus, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";

interface BuyerSearchListViewRowProps {
  buyer: BuyerSearchResult;
  onAddToList: (buyerId: string) => void;
  onContact: (buyerId: string) => void;
}

export const BuyerSearchListViewRow = ({ buyer, onAddToList, onContact }: BuyerSearchListViewRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getSubscriptionBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'trial':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Get preferred markets as array of strings
  const preferredMarkets = Array.isArray(buyer.preferred_markets) 
    ? buyer.preferred_markets 
    : [];

  // Limit badges shown in collapsed state
  const maxCollapsedBadges = 4;
  const visibleMarkets = preferredMarkets.slice(0, maxCollapsedBadges);
  const remainingCount = preferredMarkets.length - maxCollapsedBadges;

  // Get purchasing types as array
  const purchasingTypes = Array.isArray(buyer.purchasing_type) 
    ? buyer.purchasing_type 
    : [];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div 
          className={cn(
            "flex items-center justify-between p-4 cursor-pointer transition-colors",
            "hover:bg-muted/50",
            isOpen && "bg-muted/30"
          )}
          role="button"
          aria-expanded={isOpen}
        >
          {/* Left side: Avatar + Name */}
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-10 w-10 flex-shrink-0">
              <AvatarImage src={buyer.profile_image_url || undefined} alt={buyer.full_name} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(buyer.full_name)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium truncate">{buyer.full_name}</span>
          </div>

          {/* Right side: Markets + Chevron */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <div className="hidden sm:flex items-center gap-1 flex-wrap justify-end max-w-[300px]">
              {visibleMarkets.map((market) => (
                <Badge key={market} variant="outline" className="text-xs">
                  {market}
                </Badge>
              ))}
              {remainingCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  +{remainingCount} more
                </Badge>
              )}
              {preferredMarkets.length === 0 && (
                <span className="text-xs text-muted-foreground">No markets set</span>
              )}
            </div>
            <ChevronDown 
              className={cn(
                "h-5 w-5 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-180"
              )} 
            />
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className="animate-accordion-down">
        <div className="px-4 pb-4 pt-2 border-t bg-muted/20 space-y-4">
          {/* Company & Status Row */}
          <div className="flex flex-wrap items-center gap-3">
            {buyer.company_name && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span>{buyer.company_name}</span>
              </div>
            )}
            <Badge variant={getSubscriptionBadgeVariant(buyer.subscription_status)}>
              {buyer.subscription_status === 'active' ? 'Active' : 
               buyer.subscription_status === 'trial' ? 'Trial' : 
               buyer.subscription_status}
            </Badge>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              <span>{buyer.deals_annually} deals/year</span>
            </div>
            {buyer.average_close_time && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{buyer.average_close_time}</span>
              </div>
            )}
          </div>

          {/* Purchasing Types */}
          {purchasingTypes.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Purchasing Types</span>
              <div className="flex flex-wrap gap-1">
                {purchasingTypes.map((type) => (
                  <Badge key={type} variant="secondary" className="text-xs">
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* All Markets (shown in expanded view on mobile too) */}
          {preferredMarkets.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Investment Markets</span>
              <div className="flex flex-wrap gap-1">
                {preferredMarkets.map((market) => (
                  <Badge key={market} variant="outline" className="text-xs">
                    {market}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onAddToList(buyer.id);
              }}
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Add to List
            </Button>
            
            {buyer.has_viewed_properties && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onContact(buyer.id);
                }}
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Contact
              </Button>
            )}

            {buyer.has_viewed_properties && buyer.phone_number && (
              <Button
                size="sm"
                variant="ghost"
                asChild
              >
                <a href={`tel:${buyer.phone_number}`} onClick={(e) => e.stopPropagation()}>
                  <Phone className="h-4 w-4" />
                </a>
              </Button>
            )}

            {buyer.has_viewed_properties && buyer.email && (
              <Button
                size="sm"
                variant="ghost"
                asChild
              >
                <a href={`mailto:${buyer.email}`} onClick={(e) => e.stopPropagation()}>
                  <Mail className="h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
